import { HubConnectionBuilder, HubConnection, HubConnectionState, LogLevel } from '@microsoft/signalr'
import * as Sentry from '@sentry/browser'
import { API_CONFIG } from '@/api/config'

type EventHandler = (...args: unknown[]) => void

/**
 * Transient browser network failures — the API cold-starting, the user offline, or a
 * request aborted because they navigated away. Chrome throws "Failed to fetch", Firefox
 * "NetworkError when attempting to fetch resource", Safari "Load failed". A negotiate that
 * never answers is the SignalR client's own "A timeout occurred" (ORB-1000).
 */
function isTransientNetworkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /failed to fetch|networkerror|load failed|a timeout occurred/i.test(message)
}

/**
 * Failures we expect (and treat as best-effort) during connection churn — LEAVING a group
 * on teardown (unmount, route change, logout) or RE-SUBSCRIBING after an auto-reconnect. In
 * both cases the network may be gone (navigation / cold-start) or the access token has
 * already expired, so the group op throws "Failed to fetch" or "… : 401". The server
 * reconciles group membership on disconnect/reconnect regardless, and a later join retries,
 * so these are harmless. Anything else is a real bug and must still surface.
 */
function isExpectedBestEffortError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return isTransientNetworkError(error) || /:\s*401$/.test(message)
}

class SignalRService {
  private connection: HubConnection | null = null
  private groupRefCounts = new Map<string, number>()
  private subscribedConnections = new Map<string, string>()
  private getAccessToken?: () => Promise<string | undefined>
  private pendingGroupOps: Promise<void> = Promise.resolve()

  init(getAccessToken: () => Promise<string | undefined>) {
    this.getAccessToken = getAccessToken
  }

  private buildConnection(): HubConnection {
    const conn = new HubConnectionBuilder()
      .withUrl(`${API_CONFIG.BASE_URL}/api/realtime`, {
        accessTokenFactory: async () => (await this.getAccessToken?.()) ?? '',
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build()

    // Re-subscribe all active groups on reconnect (connectionId changes). This is a
    // fire-and-forget callback the SignalR client invokes, so a throw here has no caller and
    // surfaces as an unhandled rejection (ORB-408). Guard each group best-effort: swallow the
    // expected churn failures (transient blip / expired-session 401 — a later join
    // re-subscribes) and capture anything genuinely unexpected instead of letting it leak.
    conn.onreconnected(async () => {
      const connectionId = conn.connectionId
      if (!connectionId) return

      for (const group of this.groupRefCounts.keys()) {
        try {
          await this.subscribeOnServer(connectionId, group)
          // A previous reconnect may finish after a newer connection has subscribed.
          if (this.connection === conn && conn.connectionId === connectionId &&
            conn.state === HubConnectionState.Connected && this.groupRefCounts.has(group)) {
            this.subscribedConnections.set(group, connectionId)
          }
        } catch (err) {
          if (isExpectedBestEffortError(err)) {
            console.warn(`SignalR re-subscribe skipped (best-effort) for group ${group}:`, err)
          } else {
            Sentry.captureException(err)
          }
        }
      }
    })

    return conn
  }

  private async getAuthHeaders(token?: string): Promise<Record<string, string>> {
    const resolvedToken = token ?? (await this.getAccessToken?.())
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (resolvedToken) {
      headers['Authorization'] = `Bearer ${resolvedToken}`
    }
    return headers
  }

  private async subscribeOnServer(connectionId: string, group: string, token?: string): Promise<void> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/realtime/groups/subscribe`, {
      method: 'POST',
      headers: await this.getAuthHeaders(token),
      body: JSON.stringify({ connectionId, group }),
    })
    if (!response.ok) {
      throw new Error(`SignalR subscribe failed for group ${group}: ${response.status}`)
    }
  }

  private async unsubscribeOnServer(connectionId: string, group: string): Promise<void> {
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/realtime/groups/subscribe`, {
      method: 'DELETE',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ connectionId, group }),
    })
    if (!response.ok) {
      throw new Error(`SignalR unsubscribe failed for group ${group}: ${response.status}`)
    }
  }

  private enqueue<T>(op: () => Promise<T>): Promise<T> {
    const result = this.pendingGroupOps.then(() => op())
    this.pendingGroupOps = result.then(() => {}, console.error)
    return result
  }

  async joinGroup(group: string): Promise<void> {
    await this.joinGroupInternal(group, false)
  }

  /** True acquires one reference to a confirmed subscription; false needs no leaveGroup. */
  async joinGroupWithStatus(group: string): Promise<boolean> {
    return this.joinGroupInternal(group, true)
  }

  private isSubscribed(group: string): boolean {
    const connectionId = this.connection?.connectionId
    return this.connection?.state === HubConnectionState.Connected && !!connectionId &&
      this.subscribedConnections.get(group) === connectionId
  }

  private joinGroupInternal(group: string, requireSubscription: boolean): Promise<boolean> {
    return this.enqueue(async () => {
      const current = this.groupRefCounts.get(group) ?? 0

      if (current > 0) {
        const subscribed = this.isSubscribed(group)
        // Legacy callers keep their existing reference-count behavior during reconnects.
        if (!requireSubscription || subscribed) this.groupRefCounts.set(group, current + 1)
        return subscribed
      }

      if (!this.connection) {
        this.connection = this.buildConnection()
        try {
          await this.connection.start()
        } catch (err) {
          // Initial negotiate/connect can fail transiently (API cold-start, offline, or
          // the request aborted because the user navigated away). Reset so a later join
          // retries a fresh connection; swallow the expected network case and let
          // anything unexpected surface.
          this.connection = null
          if (isTransientNetworkError(err)) {
            console.warn(`SignalR connection start skipped (transient) for group ${group}:`, err)
            return false
          }
          throw err
        }
      }

      const connectionId = this.connection.connectionId
      if (!connectionId) {
        // The socket dropped into auto-reconnect right after start(). Skip the immediate
        // subscribe rather than throwing (which surfaced as an unhandled rejection); the
        // group is left untracked so a later join (e.g. on re-render) retries cleanly.
        console.warn(`SignalR connectionId unavailable after start — skipping subscribe for group "${group}"`)
        return false
      }

      // If the access token is gone (session expired mid-navigation/logout), skip the
      // doomed unauthenticated subscribe that would only 401. A later join re-establishes
      // it once the session refreshes.
      const token = await this.getAccessToken?.()
      if (!token) {
        console.warn(`SignalR subscribe skipped for group ${group} — no access token (session likely expired)`)
        return false
      }

      try {
        // Reuse the token we just fetched so the subscribe decision and the request headers
        // stay consistent — a re-fetch inside getAuthHeaders could return undefined and 401.
        await this.subscribeOnServer(connectionId, group, token)
        this.subscribedConnections.set(group, connectionId)
        const subscribed = this.isSubscribed(group)
        if (!requireSubscription || subscribed) this.groupRefCounts.set(group, 1)
        return subscribed
      } catch (err) {
        // A transient network blip here is best-effort. The group is left untracked (refcount
        // stays 0), so a later join (e.g. on re-render/re-mount) retries the subscribe. A 401
        // despite a present token is an unexpected race and is deliberately left to surface.
        if (isTransientNetworkError(err)) {
          console.warn(`SignalR subscribe skipped (transient) for group ${group}:`, err)
          return false
        }
        console.error(`Failed to subscribe to group ${group}:`, err)
        throw err
      }
    })
  }

  async leaveGroup(group: string): Promise<void> {
    return this.enqueue(async () => {
      const current = this.groupRefCounts.get(group) ?? 0
      if (current <= 0) return

      const next = current - 1
      if (next === 0) {
        this.groupRefCounts.delete(group)
        this.subscribedConnections.delete(group)

        const connectionId = this.connection?.connectionId
        if (connectionId) {
          try {
            await this.unsubscribeOnServer(connectionId, group)
          } catch (err) {
            // Teardown is best-effort: on unmount/logout the network may be gone or the
            // token already expired (401). Swallow those expected cases so they don't
            // become unhandled rejections; re-throw anything unexpected.
            if (!isExpectedBestEffortError(err)) throw err
            console.warn(`SignalR unsubscribe skipped (best-effort) for group ${group}:`, err)
          }
        }

        if (this.groupRefCounts.size === 0 && this.connection) {
          await this.connection.stop()
          this.connection = null
        }
      } else {
        this.groupRefCounts.set(group, next)
      }
    })
  }

  on(event: string, handler: EventHandler): void {
    this.connection?.on(event, handler)
  }

  off(event: string, handler: EventHandler): void {
    this.connection?.off(event, handler)
  }
}

export const signalRService = new SignalRService()
